import React, { useEffect, useState } from 'react';
import { getStampPackages, createStampPackage, updateStampPackage } from '../../../services/api';
import { Plus, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY_FORM = { name: '', quantity: '', price: '', is_active: true, sort_order: 0 };

const StampPackagesTab = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadPackages(); }, []);

  const loadPackages = async () => {
    setLoading(true);
    try {
      const res = await getStampPackages();
      setPackages(res.data?.data || []);
    } catch { setPackages([]); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setShowModal(true);
  };

  const openEdit = (pkg) => {
    setEditing(pkg);
    setForm({
      name: pkg.name,
      quantity: pkg.quantity,
      price: pkg.price,
      is_active: pkg.is_active !== false,
      sort_order: pkg.sort_order || 0,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const qty = parseInt(form.quantity);
    const price = parseFloat(form.price);
    if (!form.name.trim()) return toast.error('El nombre es requerido');
    if (!qty || qty <= 0) return toast.error('Cantidad inválida');
    if (!price || price <= 0) return toast.error('Precio inválido');

    setSaving(true);
    try {
      const data = { ...form, quantity: qty, price, price_per_stamp: Math.round((price / qty) * 100) / 100 };
      if (editing) {
        await updateStampPackage(editing.id, data);
        toast.success('Paquete actualizado');
      } else {
        await createStampPackage(data);
        toast.success('Paquete creado');
      }
      setShowModal(false);
      loadPackages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar paquete');
    } finally {
      setSaving(false);
    }
  };

  const pricePerStamp = () => {
    const qty = parseInt(form.quantity) || 0;
    const price = parseFloat(form.price) || 0;
    if (qty > 0 && price > 0) return (price / qty).toFixed(2);
    return '—';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900">Paquetes de Timbres</h2>
        <button onClick={openCreate} className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-700 text-white text-sm rounded-lg hover:bg-teal-800 font-medium transition-colors">
          <Plus className="w-4 h-4" /> Nuevo Paquete
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {packages.length === 0 ? (
          <div className="col-span-full bg-white rounded-xl border border-slate-200 p-12 text-center">
            <p className="text-slate-500">No hay paquetes. Crea uno primero.</p>
          </div>
        ) : packages.map((pkg) => (
          <div key={pkg.id} className={`bg-white rounded-xl border ${pkg.is_active !== false ? 'border-slate-200' : 'border-red-200'} p-6 space-y-3`}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">{pkg.name}</h3>
              <button onClick={() => openEdit(pkg)} className="p-1.5 hover:bg-slate-100 rounded text-slate-500" title="Editar">
                <Pencil className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-teal-700">{pkg.quantity}</span>
              <span className="text-sm text-slate-500">timbres</span>
            </div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Precio:</span>
                <span className="font-medium text-slate-900">${(pkg.price || 0).toLocaleString('es-MX')} MXN</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Precio/timbre:</span>
                <span className="font-medium text-slate-600">${(pkg.price_per_stamp || (pkg.price / pkg.quantity)).toFixed(2)}</span>
              </div>
            </div>
            <div className="flex items-center justify-between pt-1">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${pkg.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {pkg.is_active !== false ? 'Activo' : 'Inactivo'}
              </span>
              <span className="text-xs text-slate-400">Orden: {pkg.sort_order || 0}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">{editing ? 'Editar Paquete' : 'Nuevo Paquete'}</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nombre *</label>
                <input
                  type="text" required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="Paquete 100 Timbres"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Cantidad *</label>
                  <input
                    type="number" min="1" required
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Precio MXN *</label>
                  <input
                    type="number" step="0.01" min="0.01" required
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="500.00"
                  />
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <span className="text-slate-500">Precio por timbre: </span>
                <span className="font-bold text-slate-900">${pricePerStamp()} MXN</span>
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="rounded border-slate-300"
                  />
                  Activo
                </label>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-slate-700">Orden:</label>
                  <input
                    type="number" min="0"
                    value={form.sort_order}
                    onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                    className="w-16 px-2 py-1 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors disabled:opacity-50">
                  {saving ? 'Guardando...' : editing ? 'Actualizar' : 'Crear'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StampPackagesTab;
